import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/ui/select";
import { Label } from "../../../Components/ui/label";
import CreateContentSMSCard from "./CreateContentSMSCard";
import CreateContentWhatsappCard from "./CreateContentWhatsappCard";
import { getSmsPartsCount } from "../../../Pages/Campaign/CreateCampaign/utils/getSmsPartsCount";
import { useSelector } from "react-redux";
import { RootState } from "@/src/State/store";
import axios from "axios";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../../../Components/ui/hover-card";
import { Info } from "lucide-react";
import { toast } from "react-toastify";

interface Campaign {
  campaign_id: number;
  campaign_name: string;
  workspace_name: string;
  advertiser_workspace_id: number;
  status: string;
  channels: string[];
  start_date: string;
  end_date: string;
}

interface Props {
  campaign: Campaign | null;
  onSend: (payload: any) => void;
  sendTrigger: number;
}

const QuickSendCard: React.FC<Props> = ({ campaign, onSend, sendTrigger }) => {

  // ✅ Stage 1 — Operators
  const [operators, setOperators] = useState<any[]>([]);

  // ✅ Stage 2 — Configs
  const [configs, setConfigs] = useState<any[]>([]);
  const [selectedChannelServerUrl, setSelectedChannelServerUrl] = useState<string>("");

  // ✅ Stage 3 — Templates
  const [templates, setTemplates] = useState<any[]>([]);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [templateContents, setTemplateContents] = useState<Record<string, string>>({});

  // ✅ Dropdown selections
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [selectedOperator, setSelectedOperator] = useState<string>("");
  const [selectedOperatorConfig, setSelectedOperatorConfig] = useState<string>("");

  // ✅ Contacts
  const [contacts, setContacts] = useState<string>("");
  const [contactError, setContactError] = useState<string>("");

  // ✅ SMS parts
  const [smsPartsCount, setSmsPartsCount] = useState(1);
  const [smsShowWarning, setSmsShowWarning] = useState(false);

  const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );

  const availableChannels = (campaign?.channels || []).filter(Boolean);
  const isSmsSelected = selectedChannel === "SMS";

  // ✅ Channel ID mapping
  const getChannelId = (channelType: string): number => {
    if (channelType === "WhatsApp") return 1;
    if (channelType === "SMS") return 2;
    return 0;
  };

  // ✅ Build template contents when templates change
  useEffect(() => {
    if (templates.length === 0) return;
    const contents: Record<string, string> = {};
    templates.forEach((t: any) => {
      const tt =
        typeof t.translated_template === "string"
          ? JSON.parse(t.translated_template)
          : t.translated_template;

      if (selectedChannel?.toLowerCase() === "whatsapp") {
        contents[t.operator_mapping_id] = JSON.stringify(
          tt?.template?.components ?? []
        );
      } else {
        contents[t.operator_mapping_id] = tt?.template?.message ?? "";
      }
    });
    setTemplateContents(contents);
  }, [templates]);

  // ✅ Send trigger from parent
  useEffect(() => {
    if (sendTrigger === 0) return;
    handleSend();
  }, [sendTrigger]);

  // ✅ On campaign load: auto-select channel if only one available
  useEffect(() => {
    if (!campaign?.campaign_id) return;
    if (availableChannels.length === 1) {
      setSelectedChannel(availableChannels[0]);
    }
  }, [campaign]);

  // ✅ Stage 1: Fetch operators when channel is selected
  useEffect(() => {
    if (!selectedChannel || !campaign?.campaign_id) return;
    const channelId = getChannelId(selectedChannel);
    getQuickSendOperators(channelId);
  }, [selectedChannel]);

  // ✅ SMS parts count
  useEffect(() => {
    if (!previewTemplateId) {
      setSmsPartsCount(1);
      setSmsShowWarning(false);
      return;
    }
    const tmpl = templates.find(
      (t) => t.operator_mapping_id.toString() === previewTemplateId
    );
    if (selectedChannel === "SMS") {
      const content = templateContents[previewTemplateId] || "";
      const { parts } = getSmsPartsCount(content);
      setSmsPartsCount(parts);
      setSmsShowWarning(parts > 1);
    } else {
      setSmsPartsCount(1);
      setSmsShowWarning(false);
    }
  }, [previewTemplateId, templateContents, templates]);

  // ─────────────────────────────────────────
  // API CALLS
  // ─────────────────────────────────────────

  // ✅ Stage 1
  const getQuickSendOperators = async (channelId: number) => {
    try {
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetQuickSendOperators?campaignId=${campaign?.campaign_id}&channelId=${channelId}`
      );
      if (response.data.status === "Success") {
        setOperators(response.data.operators);
      } else {
        setOperators([]);
      }
    } catch (error) {
      console.error("Error fetching operators:", error);
      setOperators([]);
    }
  };

  // ✅ Stage 2
  const getQuickSendConfigs = async (operatorId: number) => {
    try {
      const channelId = getChannelId(selectedChannel);
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetQuickSendConfigs?campaignId=${campaign?.campaign_id}&channelId=${channelId}&operatorId=${operatorId}`
      );
      if (response.data.status === "Success") {
        setConfigs(response.data.configs);
      } else {
        setConfigs([]);
      }
    } catch (error) {
      console.error("Error fetching configs:", error);
      setConfigs([]);
    }
  };

  // ✅ Stage 3
  const getQuickSendTemplates = async (channelConfigId: number) => {
    try {
      const channelId = getChannelId(selectedChannel);
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetQuickSendTemplate?campaignId=${campaign?.campaign_id}&channelId=${channelId}&operatorId=${selectedOperator}&channelConfigId=${channelConfigId}`
      );
      if (response.data.status === "Success") {
        setTemplates(response.data.templates);
        const firstTemplate = response.data.templates[0];
        setPreviewTemplateId(firstTemplate ? firstTemplate.operator_mapping_id.toString() : null);
      } else {
        setTemplates([]);
        setPreviewTemplateId(null);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
      setPreviewTemplateId(null);
    }
  };

  // ─────────────────────────────────────────
  // CONTACTS VALIDATION
  // ─────────────────────────────────────────

  const validateContacts = (rawContacts: string): string[] | null => {
    if (!rawContacts.trim()) {
      setContactError("Please enter at least one contact number.");
      return null;
    }

    const numbers = rawContacts
      .split(/[,\n]/)
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    if (numbers.length === 0) {
      setContactError("Please enter at least one contact number.");
      return null;
    }

    for (const num of numbers) {
      // ✅ Allow optional + at start, then digits only
      if (!/^\+?\d+$/.test(num)) {
        setContactError(`Invalid number: "${num}". Only digits allowed, + permitted at start.`);
        return null;
      }

      // ✅ Min 10, Max 15 digits (excluding +)
      const digitsOnly = num.replace("+", "");
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        setContactError(`Invalid number: "${num}". Must be between 10 and 15 digits.`);
        return null;
      }
    }

    // ✅ Duplicate check
    const seen = new Set<string>();
    for (const num of numbers) {
      if (seen.has(num)) {
        setContactError(`Duplicate number found: "${num}".`);
        return null;
      }
      seen.add(num);
    }

    setContactError("");
    return numbers;
  };

  // ─────────────────────────────────────────
  // SEND
  // ─────────────────────────────────────────

  const handleSend = () => {
    // ✅ Validate template selected
    if (!previewTemplateId) {
      toast.error("Please select a template before sending.");
      return;
    }

    // ✅ Validate contacts
    const validatedNumbers = validateContacts(contacts);
    if (!validatedNumbers) return;

    const selectedTemplate = templates.find(
      (t) => t.operator_mapping_id.toString() === previewTemplateId
    );
    if (!selectedTemplate) return;

  const translatedTemplate =
    typeof selectedTemplate.translated_template === "string"
      ? JSON.parse(selectedTemplate.translated_template)
      : { ...selectedTemplate.translated_template }; // clone to avoid mutation

  // ✅ Check HEADER format
  const headerComponent = translatedTemplate?.template?.components?.find(
    (comp: any) => comp.type === "HEADER"
  );

  const headerFormat = headerComponent?.format;

// Normalize header format to uppercase
const normalizedFormat = headerFormat?.toUpperCase();

// ✅ Remove binary_data if IMAGE / VIDEO / DOCUMENT (case-insensitive)
if (
  ["IMAGE", "VIDEO", "DOCUMENT"].includes(normalizedFormat) &&
  translatedTemplate?.media_config?.binary_data
) {
  delete translatedTemplate.media_config.binary_data;
}

    const payload = {
      type: "quick_send",                               // ✅ fixed	
      account_id: null,
      channel_config_id: selectedTemplate.channel_config_id,
      operator_id: selectedTemplate.operator_id,
      operator_mapping_id: selectedTemplate.operator_mapping_id,
      campaign_id: campaign?.campaign_id,
      channel_id: selectedTemplate.channel_id,
      template_id: selectedTemplate.template_id,        // ✅ direct column now
      msisdn: validatedNumbers,
      channelserver_url: selectedChannelServerUrl,
      message: translatedTemplate,                      // ✅ entire translated_template as-is
    };

    onSend(payload);
    console.log("my quicksend paylaod", payload);
  };

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  return (
    <div className="flex gap-8 p-4 bg-white rounded-xl border border-gray-200 shadow-sm w-full max-w-[1600px] mx-auto my-auto">

      {/* LEFT SIDE - FORM */}
      <div className="flex-1 p-4 bg-white rounded-xl space-y-4 self-start">
        <h1 className="text-2xl font-semibold text-gray-900">Quick Send</h1>

        {/* Campaign Name */}
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-gray-700">Campaign Name</Label>
          <input
            value={campaign?.campaign_name || ""}
            readOnly
            className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-600"
          />
        </div>

        {/* Channel Type */}
        <div className="flex flex-col gap-1">
          <Label className="text-sm font-medium text-gray-700">Channel Type</Label>
          {availableChannels.length === 1 ? (
            <input
              value={availableChannels[0]}
              readOnly
              className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-600 h-10"
            />
          ) : (
            <Select
              value={selectedChannel}
              onValueChange={(value) => {
                setSelectedChannel(value);
                setSelectedOperator("");
                setSelectedOperatorConfig("");
                setSelectedChannelServerUrl("");
                setOperators([]);
                setConfigs([]);
                setTemplates([]);
                setPreviewTemplateId(null);
                setTemplateContents({});
              }}
            >
              <SelectTrigger className="rounded px-3 py-2 text-gray-500 h-10">
                <SelectValue placeholder="Select Channel Type" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {availableChannels.map((channel) => (
                  <SelectItem key={channel} value={channel}>
                    {channel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!selectedChannel && (
            <p className="text-xs italic text-red-500">Select Channel Type</p>
          )}
        </div>

        {/* Operator + Operator Config */}
        <div className="flex gap-4 items-start">

          {/* Operator */}
          <div className="flex flex-col gap-1 flex-1">
            <Label className="text-sm font-medium text-gray-700">Operator</Label>
            <Select
              value={selectedOperator}
              disabled={!selectedChannel}
              onValueChange={(value) => {
                setSelectedOperator(value);
                setSelectedOperatorConfig("");
                setSelectedChannelServerUrl("");
                setConfigs([]);
                setTemplates([]);
                setPreviewTemplateId(null);
                getQuickSendConfigs(Number(value));
              }}
            >
              <SelectTrigger className="rounded px-3 py-2 text-gray-500 h-10">
                <SelectValue placeholder="Select Operator" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {operators.length > 0 ? (
                  operators.map((item: any) => (
                    <SelectItem
                      key={item.operator_id}
                      value={item.operator_id.toString()}
                    >
                      {item.operator_name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-gray-400 text-sm">No operators found</div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Operator Config */}
          <div className="flex flex-col gap-1 flex-1">
            <Label className="text-sm font-medium text-gray-700">Operator Config</Label>
            <Select
              value={selectedOperatorConfig}
              disabled={!selectedOperator}
              onValueChange={(value) => {
                setSelectedOperatorConfig(value);
                // ✅ Store channelserver_url
                const selectedConfig = configs.find(
                  (c: any) => c.channel_config_id.toString() === value
                );
                setSelectedChannelServerUrl(selectedConfig?.channelserver_url || "");
                // ✅ Fetch templates for Stage 3
                getQuickSendTemplates(Number(value));
              }}
            >
              <SelectTrigger className="rounded px-3 py-2 text-gray-500 h-10">
                <SelectValue placeholder="Select Operator Config" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {configs.length > 0 ? (
                  configs.map((item: any) => (
                    <SelectItem
                      key={item.channel_config_id}
                      value={item.channel_config_id.toString()}
                    >
                      {item.channel_config_name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-gray-400 text-sm">No configs found</div>
                )}
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Contacts */}
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-gray-700">Contacts</Label>
          {isSmsSelected && (
            <div className="flex items-center gap-1 text-amber-600 text-xs">
              <span className="text-sm">⚠️</span>
              <span>SMS messages in quick send will be sent only for operator test numbers.</span>
            </div>
          )}
          <textarea
            className={`border rounded-md p-2 text-sm min-h-[80px] resize-none ${contactError ? "border-red-500" : "border-gray-300"
              }`}
            value={contacts}
            onChange={(e) => {
              const value = e.target.value;
              const count = value.split(/[,\n]/).map(c => c.trim()).filter(c => c.length > 0).length;
              if (count > 10) {
                {
                  contactError && (
                    <p className="text-xs text-red-500 italic">{contactError}</p>
                  )
                }
                return;
              }
              setContacts(value);
              setContactError("");
            }}
            placeholder="919876543210,919123456789&#10;"
          />
          {/* ✅ Contact error message */}
          {contactError && (
            <p className="text-xs text-red-500">{contactError}</p>
          )}
          <div className="text-xs text-gray-500">
            <p>Enter contact numbers with country code.</p>
          </div>

          {/* ✅ SMS parts count */}
          {previewTemplateId &&
            selectedChannel === "SMS" && (
              <div className="border rounded-md p-3 bg-gray-50 mt-2 text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center justify-between">
                <div className="flex gap-6">
                  <span>CHARACTERS: <span className="font-bold">{(templateContents[previewTemplateId] || "").length}</span></span>
                  <span>SMS PARTS: <span className="font-bold">{smsPartsCount}</span></span>
                </div>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <span className="cursor-pointer">
                      <Info className="w-4 h-4 text-blue-400 hover:opacity-80" />
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 p-4 text-sm bg-white shadow-lg border border-gray-200 rounded-xl">
                    <p className="font-semibold text-gray-600 mb-2">Why is the message split?</p>
                    <p className="text-gray-500 mb-2">Most messages use a standard encoding called <strong>GSM-7</strong>, which allows up to <strong>160 characters</strong> in one SMS.</p>
                    <p className="text-gray-500 mb-2">If your message includes special symbols, emojis, or characters from non-English languages, it switches to a different encoding called <strong>UCS-2</strong>, which allows only <strong>70 characters</strong> per SMS.</p>
                    <p className="text-gray-500">When your message goes beyond these limits, it is automatically split into multiple parts.</p>
                  </HoverCardContent>
                </HoverCard>
              </div>
            )}
        </div>

      </div>

      {/* RIGHT SIDE - PREVIEW */}
      <div className="w-[420px] flex flex-col min-h-[520px]">
        <div className="mb-3">
          <Label className="text-sm font-medium text-gray-700">Preview Template</Label>
          <Select
            value={previewTemplateId || ""}
            onValueChange={(value) => {
              setPreviewTemplateId(value);
            }}
          >
            <SelectTrigger className="rounded px-3 py-2">
              <SelectValue placeholder="Select a template to preview" />
            </SelectTrigger>
            <SelectContent className="z-[10001]">
              {templates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No templates available — select operator config first
                </div>
              ) : (
                // ✅ Stage 3 — list template names in dropdown
                templates.map((t: any, index: number) => (
                  <SelectItem
                    key={`${t.operator_mapping_id}-${index}`}
                    value={t.operator_mapping_id.toString()}
                  >
                    {t.template_name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Preview Card */}
        {!previewTemplateId ? (
          <div className="border rounded-lg p-8 flex-1 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium mb-2">No Template Selected</p>
              <p className="text-sm">Select operator config, then choose a template</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            {selectedChannel === "SMS" ? (
              <CreateContentSMSCard
                content={templateContents[previewTemplateId] || ""}
                partsCount={smsPartsCount}
                showWarning={smsShowWarning}
                onContentChange={(val) =>
                  setTemplateContents((prev) => ({
                    ...prev,
                    [previewTemplateId]: val,
                  }))
                }
              />
            ) : (
              <CreateContentWhatsappCard
                content={templateContents[previewTemplateId] || ""}
                mediaConfig={(() => {
                  const t = templates.find(
                    (t) => t.operator_mapping_id.toString() === previewTemplateId
                  );
                  if (!t?.media_config) return null;
                  return typeof t.media_config === "string"
                    ? JSON.parse(t.media_config)
                    : t.media_config;
                })()}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickSendCard;